import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowUpRight, MapPin, Package } from 'lucide-react';
import { motion } from 'framer-motion';

export const ProductCard = ({ product, index = 0 }) => {
  const { t } = useLanguage();

  return (
    <Link
      to={`/products/${product.slug}`}
      className="group block bg-white border border-border hover:border-secondary-400 transition-all duration-500 overflow-hidden"
      data-testid={`product-card-${product.slug}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        <img
          src={product.images[0]}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        
        {/* Category Badge */}
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1.5 bg-white text-[10px] font-mono uppercase tracking-[0.15em] text-primary-900">
            {product.category_name}
          </span>
        </div>

        {/* Hover Arrow */}
        <div className="absolute bottom-4 right-4 w-12 h-12 bg-secondary-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-4 group-hover:translate-y-0">
          <ArrowUpRight className="w-5 h-5 text-primary-900" />
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-lg font-heading font-bold text-primary-900 group-hover:text-secondary-600 transition-colors mb-2">
          {product.name}
        </h3>
        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
          {product.description}
        </p>

        <div className="flex items-center gap-6 text-xs text-muted-foreground pt-4 border-t border-border">
          <div className="flex items-center gap-1.5">
            <Package className="w-3.5 h-3.5 text-secondary-500" />
            <span className="font-mono">{product.min_order}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-secondary-500" />
            <span>{product.origin}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export const CategoryCard = ({ category, size = 'normal' }) => {
  const isLarge = size === 'large';
  
  return (
    <Link
      to={`/products?category=${category.slug}`}
      className="group relative block overflow-hidden bg-primary-900 transition-all duration-500 h-full"
      data-testid={`category-card-${category.slug}`}
    >
      {/* Image */}
      <div className="absolute inset-0">
        <img
          src={category.image}
          alt={category.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-900 via-primary-900/60 to-transparent" />
      </div>
      
      {/* Content Overlay */}
      <div className={`relative h-full flex flex-col justify-end ${isLarge ? 'min-h-[500px] p-10' : 'min-h-[240px] p-6'}`}>
        <span className="text-xs font-mono uppercase tracking-[0.2em] text-secondary-400 mb-2">
          {category.product_count} Products
        </span>
        <h3 className={`font-heading font-bold text-white mb-3 ${isLarge ? 'text-3xl md:text-4xl' : 'text-xl'}`}>
          {category.name}
        </h3>
        {isLarge && (
          <p className="text-white/60 text-sm line-clamp-2 mb-6 max-w-md">
            {category.description}
          </p>
        )}
        
        {/* Arrow */}
        <div className="w-12 h-12 border border-white/20 flex items-center justify-center group-hover:bg-secondary-500 group-hover:border-secondary-500 transition-all duration-300">
          <ArrowUpRight className="w-5 h-5 text-white group-hover:text-primary-900 transition-colors" />
        </div>
      </div>
    </Link>
  );
};

export const FeaturedProductCard = ({ product }) => {
  return (
    <Link
      to={`/products/${product.slug}`}
      className="group flex flex-col lg:flex-row bg-white border border-border hover:border-secondary-400 transition-all duration-500 overflow-hidden"
      data-testid={`featured-product-${product.slug}`}
    >
      {/* Image */}
      <div className="relative w-full lg:w-1/2 aspect-[16/10] lg:aspect-auto overflow-hidden">
        <img
          src={product.images[0]}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute top-6 left-6">
          <span className="px-4 py-2 bg-primary-900 text-[10px] font-mono uppercase tracking-[0.15em] text-white">
            {product.category_name}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-8 lg:p-12 flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl lg:text-3xl font-heading font-bold text-primary-900 group-hover:text-secondary-600 transition-colors mb-4">
            {product.name}
          </h3>
          <p className="text-muted-foreground mb-8 line-clamp-3 leading-relaxed">
            {product.description}
          </p>
          <div className="flex items-center gap-8 mb-8">
            <div>
              <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground block mb-1">Min Order</span>
              <span className="font-mono font-bold text-primary-900">{product.min_order}</span>
            </div>
            <div>
              <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground block mb-1">Origin</span>
              <span className="font-bold text-primary-900">{product.origin}</span>
            </div>
            <div>
              <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground block mb-1">Certified</span>
              <span className="font-bold text-primary-900">{product.certifications.join(', ')}</span>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.1em] text-secondary-600 group-hover:text-secondary-500">
            View Details
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </motion.div>
      </div>
    </Link>
  );
};
